import base from "../middlewares/interceptors";
import {
  getPaginatedRolesListUrl,
  getUserRoleUrl,
  createRoleUrl,
  updateRoleUrl,
  deleteRoleUrl,
  getCasbinPoliciesUrl,
  bulkUpdateCasbinPoliciesUrl,
} from "./constants";
import {
  CasbinPolicyResponse,
  BulkCasbinPolicyRequest,
  GetRolePermissionsParams,
  UpdateRolePermissionsParams,
} from "../types/permissions";

// Get paginated roles list
export const getPaginatedRolesList = async ({
  page,
  pSize,
  searchTerm,
  signal,
  isActive = "true",
}: any) => {
  try {
    let url = `${getPaginatedRolesListUrl}?page=${page}&size=${pSize}`;

    // Add is_active parameter if not "all"
    if (isActive !== "all") {
      const isActiveBoolean = isActive === "true" ? "true" : "false";
      url += `&is_active=${isActiveBoolean}`;
    }

    // Add search parameter if searchTerm is provided and has minimum 3 characters
    if (searchTerm && searchTerm.trim().length >= 3) {
      url += `&role_name=${encodeURIComponent(searchTerm.trim())}`;
    }

    const response = await base.get(url, {
      signal, // Add abort signal for request cancellation
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching paginated roles list:", error);
    throw error;
  }
};

// Get role by ID
export const getRoleById = async (id: string | number) => {
  try {
    const response = await base.get(`${getUserRoleUrl}/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching role by ID:", error);
    throw error;
  }
};

// Create new role
export const createRole = async (payload: {
  role_name: string;
  role_description: string;
  role_type?: string;
}) => {
  try {
    const response = await base.post(createRoleUrl, payload);
    return response.data;
  } catch (error) {
    console.error("Error creating role:", error);
    throw error;
  }
};

// Update role
export const updateRole = async (
  id: string | number,
  payload: { role_name: string; role_description: string; role_type?: string }
) => {
  try {
    const response = await base.put(`${updateRoleUrl}/${id}`, payload);
    return response.data;
  } catch (error) {
    console.error("Error updating role:", error);
    throw error;
  }
};

// Delete role
export const deleteRole = async (id: string | number) => {
  try {
    const response = await base.delete(`${deleteRoleUrl}/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting role:", error);
    throw error;
  }
};

// Permission Management Functions

// Get role permissions
export const getRolePermissions = async (
  params: GetRolePermissionsParams
): Promise<CasbinPolicyResponse> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("role_id", params.role_id.toString());

    if (params.is_active !== undefined) {
      queryParams.append("is_active", params.is_active.toString());
    }

    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }

    if (params.size !== undefined) {
      queryParams.append("size", params.size.toString());
    }

    const response = await base.get(
      `${getCasbinPoliciesUrl}?${queryParams.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching role permissions:", error);
    throw error;
  }
};

// Update role permissions (bulk update)
export const updateRolePermissions = async (
  params: UpdateRolePermissionsParams
) => {
  try {
    const payload: BulkCasbinPolicyRequest = {
      user_role_id: params.roleId,
      policies: params.policies,
    };

    const response = await base.put(bulkUpdateCasbinPoliciesUrl, payload);
    return response.data;
  } catch (error) {
    console.error("Error updating role permissions:", error);
    throw error;
  }
};

// Get all available entities and actions (for permission configuration)
export const getAvailablePermissions = async () => {
  try {
    // This could be an API call to get available entities/actions
    // For now, we'll use the static configuration
    const { PERMISSION_CATEGORIES } = await import(
      "../config/permissionsConfig"
    );
    return PERMISSION_CATEGORIES;
  } catch (error) {
    console.error("Error fetching available permissions:", error);
    throw error;
  }
};

/**
 * Reactivate role
 * @param roleId - The ID of the role to reactivate
 * @returns Promise with reactivation result
 */
export const reactivateRole = async (roleId: string | number) => {
  try {
    const response = await base.put(`${getUserRoleUrl}/${roleId}/reactivate`);
    return response.data;
  } catch (error) {
    console.error("Error reactivating role:", error);
    throw error;
  }
};
